/**
 * L'état d'interface de l'onglet « Offres de cours » : l'onglet demandé, la feuille d'une offre
 * (son étape), la réponse en route et son refus. Pur, testé.
 *
 * Port de `ProposalsTabScreen.js` (`activeTab`, `loadingItems`, les deux `DialogM` enchaînés) : la
 * feuille reste ouverte pendant l'envoi (le bouton tourne, la carte dit « Envoi en cours… ») et se
 * ferme à la réponse ; un refus se dit DANS l'étape (l'audit : « déplacé »), plus en boîte par-dessus.
 * Une offre demandée par « Ma journée » (« Accepter », « Voir ») s'ouvre dès qu'elle est dans la liste.
 */
import { type UITutorOffer, type UITutorOfferFlowStep, type UITutorOfferKind } from "../../../types/tutorDispoOffres"
import { type TutorOfferAnswer } from "./useTutorOfferAnswers"

export type TutorOffersOpenRequest = { offerId: number; step: UITutorOfferFlowStep }

export type TutorOffersUiState = {
    requestedKind: UITutorOfferKind | null
    flow: { offerId: number; step: UITutorOfferFlowStep } | null
    sending: { offerId: number; answer: TutorOfferAnswer } | null
    flowError: string | null
    /** Une offre que « Ma journée » a demandé d'ouvrir, pas encore trouvée dans la liste. */
    pendingOpen: TutorOffersOpenRequest | null
}

export type TutorOffersUiAction =
    | { type: "SELECT_TAB"; kind: UITutorOfferKind }
    | { type: "OPEN_FLOW"; offerId: number; step: UITutorOfferFlowStep }
    | { type: "GO_STEP"; step: UITutorOfferFlowStep }
    | { type: "CLOSE_FLOW" }
    | { type: "SENDING"; offerId: number; answer: TutorOfferAnswer }
    | { type: "SENT" }
    | { type: "FAILED"; message: string }
    | { type: "REQUEST_OPEN"; kind: UITutorOfferKind | null; request: TutorOffersOpenRequest }

export const initialTutorOffersUiState: TutorOffersUiState = {
    requestedKind: null,
    flow: null,
    sending: null,
    flowError: null,
    pendingOpen: null,
}

export function tutorOffersUiReducer(state: TutorOffersUiState, action: TutorOffersUiAction): TutorOffersUiState {
    switch (action.type) {
        case "SELECT_TAB":
            return { ...state, requestedKind: action.kind, pendingOpen: null }
        case "OPEN_FLOW":
            return {
                ...state,
                flow: { offerId: action.offerId, step: action.step },
                flowError: null,
                pendingOpen: null,
            }
        case "GO_STEP":
            return state.flow == null
                ? state
                : { ...state, flow: { ...state.flow, step: action.step }, flowError: null }
        case "CLOSE_FLOW":
            return { ...state, flow: null, flowError: null }
        case "SENDING":
            return { ...state, sending: { offerId: action.offerId, answer: action.answer }, flowError: null }
        case "SENT":
            return { ...state, sending: null, flow: null, flowError: null }
        case "FAILED":
            return { ...state, sending: null, flowError: action.message }
        case "REQUEST_OPEN":
            return { ...state, requestedKind: action.kind ?? state.requestedKind, pendingOpen: action.request }
        default:
            return state
    }
}

/** L'offre que la feuille montre ; `null` quand elle n'est plus dans la liste : la feuille ne se montre pas. */
export function tutorOfferInFlow(state: TutorOffersUiState, offers: UITutorOffer[]): UITutorOffer | null {
    if (state.flow == null) return null
    return offers.find((offer) => offer.id === state.flow?.offerId) ?? null
}

/** L'offre demandée par « Ma journée », si la liste la montre en attente. */
export function tutorOfferToOpen(state: TutorOffersUiState, offers: UITutorOffer[]): UITutorOffer | null {
    const wanted = state.pendingOpen
    if (wanted == null) return null
    return offers.find((offer) => offer.id === wanted.offerId && offer.status === "pending") ?? null
}
